import { IonCol, IonGrid, IonRow, IonText } from "@ionic/react";
import { isPast, isToday, isTomorrow } from "date-fns";
import { useCallback, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import SecondaryButton from "@components/buttons/SecondaryButton";
import TimeRange from "@components/dates/TimeRange";
import styles from "@components/expert/ExpertDashboardConsults.module.css";
import Tag from "@components/labels/Tag";
import Nbsp from "@components/Nbsp";
import Icon from "@components/text/Icon";
import LargeText from "@components/text/LargeText";
import TextWithAvatar from "@components/TextWithAvatar";
import InternalLink from "@components/typography/InternalLink";
import useMyExpert from "@hooks/useMyExpert";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import { GetExpertConsultsQuery } from "@typing/Generated";
import { groupByTimestampDay } from "@utils/modelUtils";
import { journeyRoute } from "@utils/routeUtils";

type Props = {
  expertConsults: GetExpertConsultsQuery["expertConsults"];
  expertId: string;
};

const ExpertDashboardConsults = ({ expertConsults, expertId }: Props) => {
  const intl = useIntl();
  const myExpert = useMyExpert();
  const allConsults = useNodesFromConnection(expertConsults);
  const consultsByDay = useMemo(() => {
    if (allConsults) {
      return groupByTimestampDay(allConsults, consult => consult.startTime, intl, true);
    }
  }, [allConsults, intl]);

  const consultCountAndDateForDay = useCallback(
    (count: number, date: Date) => {
      if (isToday(date)) {
        return intl.formatMessage({ id: "pages.expert.dashboard.days.today" }, { count });
      }
      if (isTomorrow(date)) {
        return intl.formatMessage({ id: "pages.expert.dashboard.days.tomorrow" }, { count });
      }
      return intl.formatMessage(
        { id: "pages.expert.dashboard.days.weekday" },
        { count, day: intl.formatDate(date, { weekday: "long" }) }
      );
    },
    [intl]
  );

  return (
    <>
      <div className="ion-margin-bottom">
        <div className={styles.consultHeading}>
          <LargeText>
            <FormattedMessage id="pages.expert.dashboard.boxHeadings.upcomingConsults" />
          </LargeText>
          <SecondaryButton
            routerLink={`/expert/consults?expertId=${expertId}`}
            textKey="pages.expert.dashboard.viewAllConsults"
          />
        </div>
      </div>
      {!consultsByDay?.length && (
        <p>
          <FormattedMessage id="pages.expert.dashboard.noUpcomingConsults" />
        </p>
      )}
      {consultsByDay?.map(day => (
        <div className="ion-margin-bottom" key={day.date}>
          <div className={styles.day}>
            <IonText>{consultCountAndDateForDay(day.models.length, new Date(day.models[0].startTime))}</IonText>
          </div>
          {day.models.map((consult, index) => {
            const colorForExpertTag =
              consult.expert.id === consult.journey.primaryExpert?.id ? "primary-300" : "secondary-500";

            return (
              <div
                className={`${styles.consult} ${index < day.models.length - 1 ? styles.hasNext : ""} ${
                  isPast(consult.startTime) ? styles.pastConsult : ""
                }`}
                key={consult.id}
              >
                <IonGrid>
                  <IonRow>
                    <IonCol className={styles.time}>
                      <div className={styles.timeContent}>
                        <Icon icon="clock" size="small" />
                        <TimeRange endTime={consult.endTime} startTime={consult.startTime} />
                      </div>
                    </IonCol>
                    <IonCol>
                      {consult.invites
                        .filter(invite => invite.client)
                        .map((invitee, index) => (
                          <div className={styles.purpleText} key={invitee.client?.id}>
                            <IonText>
                              {invitee.client?.fullName}
                              {index < consult.invites.length - 1 && (
                                <>
                                  <FormattedMessage id="dictionary.punctuation.comma" />
                                  <Nbsp />
                                </>
                              )}
                            </IonText>
                          </div>
                        ))}
                      <div className={styles.careProfileName}>
                        <IonText>
                          {intl.formatMessage(
                            { id: "pages.expert.dashboard.lovedOne" },
                            {
                              name: (
                                <InternalLink
                                  className={styles.name}
                                  to={journeyRoute({
                                    journeyId: consult.journey.id,
                                    linkAfterJourney: `/consults`,
                                    myExpert
                                  })}
                                >
                                  {consult.careProfileName}
                                </InternalLink>
                              )
                            }
                          )}
                        </IonText>
                      </div>
                    </IonCol>
                  </IonRow>
                  <IonRow>
                    {myExpert?.id === consult.expert.id ? (
                      <Tag
                        color={colorForExpertTag}
                        text={intl.formatMessage(
                          {
                            id: `pages.expert.myConsult`
                          },
                          {
                            firstName: consult.expert.firstName
                          }
                        )}
                      />
                    ) : (
                      <TextWithAvatar
                        color={colorForExpertTag}
                        photoUrl={consult.expert.photoUrl}
                        text={consult.expert.firstName}
                      />
                    )}
                  </IonRow>
                </IonGrid>
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
};

export default ExpertDashboardConsults;
